/**
 * Customer & Segment API
 */
import type { ItemResponse, ListResponse, DeleteResponse } from '../../types/api.js';
import type { Customer, Segment } from '../../types/models.js';
import { apiRequest } from './core.js';

export const customersApi = {
  list: (params?: { search?: string; status?: string; segment?: string; type?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
    const searchParams = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v !== undefined) searchParams.set(k, String(v)); });
    return apiRequest<ListResponse<Customer>>(`/customers?${searchParams}`);
  },
  get: (id: string) => apiRequest<ItemResponse<Customer>>(`/customers/${id}`),
  create: (data: Partial<Customer>) => apiRequest<ItemResponse<Customer>>('/customers', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Customer>) => apiRequest<ItemResponse<Customer>>(`/customers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest<DeleteResponse>(`/customers/${id}`, { method: 'DELETE' }),
  bulkUpdate: (ids: string[], data: Partial<Customer>) => apiRequest<ItemResponse<{ updated: number }>>('/customers/bulk-update', { method: 'POST', body: JSON.stringify({ ids, data }) }),
  importCsv: (customers: Array<{ email: string; firstName?: string; lastName?: string; phone?: string; tags?: string[] }>) =>
    apiRequest<ItemResponse<{ total: number; created: number; updated: number; errors: number }>>('/customers/import', { method: 'POST', body: JSON.stringify({ customers }) }),
};

export type SegmentFieldType = 'number' | 'date' | 'string' | 'boolean' | 'array';

export interface SegmentField {
  key: string;
  label: string;
  type: SegmentFieldType;
  operators: string[];
}

export interface SegmentRule {
  field: string;
  operator: string;
  value: string | number | boolean;
}

export interface SegmentDefinition {
  rules: SegmentRule[];
  combinator: 'AND' | 'OR';
}

export const segmentsApi = {
  list: () => apiRequest<ListResponse<Segment>>('/segments'),
  get: (id: string) => apiRequest<ItemResponse<Segment>>(`/segments/${id}`),
  /** Field + operator catalog that drives the visual builder dropdowns. */
  getFields: () => apiRequest<ItemResponse<SegmentField[]>>('/segments/fields'),
  /** Live customer count for an unsaved definition (no write). */
  preview: (definition: SegmentDefinition) =>
    apiRequest<ItemResponse<{ count: number }>>('/segments/preview', { method: 'POST', body: JSON.stringify({ definition }) }),
  create: (data: { name: string; description?: string; definition: SegmentDefinition; isActive?: boolean }) =>
    apiRequest<ItemResponse<Segment>>('/segments', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: { name?: string; description?: string; definition?: SegmentDefinition; isActive?: boolean }) =>
    apiRequest<ItemResponse<Segment>>(`/segments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest<DeleteResponse>(`/segments/${id}`, { method: 'DELETE' }),
  getCustomers: (id: string) => apiRequest<ListResponse<Customer>>(`/segments/${id}/customers`),
  refresh: (id: string) => apiRequest<ItemResponse<{ total: number }>>(`/segments/${id}/refresh`, { method: 'POST' })
};
